/**
 * The page's horizontal composition: full-width sections separated by a rule, and cells
 * butted against each other by a hairline.
 *
 * ## What this replaces, and why it is a shared component rather than a convention
 *
 * Every screen in the product is a vertical stack of bordered, rounded cards on
 * `surface-raised`, each 16px from the next. Three consequences, all of them measured on
 * the built screens rather than argued from taste:
 *
 * 1. **A card inside a card inside a card.** `RunStrip` is a bordered raised block inside
 *    a `Panel`; `ProvenanceNotice` is a bordered sunken block inside a `Panel`; a
 *    `DeepestSteps` list item carries its own border inside both. Three borders inside
 *    200px. That is the "back-office" reading the redesign is trying to lose, and it is
 *    not caused by any one of those components — it is caused by every one of them
 *    insisting on its own frame wherever it is mounted.
 *
 * 2. **The gutter wastes the width the design asks for.** design/09 §6 sets no maximum
 *    content width. A 16px gutter between two rounded cards spends horizontal space on
 *    saying "these are two things", which a 1px rule says in a sixteenth of the room.
 *
 * 3. **Nothing can be restyled from the shared layer.** The composition lives in each
 *    screen's own JSX, so changing it means editing every screen. `Band` and `CellGrid`
 *    are the seam that makes the next change one file instead of thirty-nine.
 *
 * ## `gap-hairline`, not `gap-px`
 *
 * The 1px between two cells is the border width, and it is named as one. `tokens/layout.json`'s
 * space scale carries its own instruction — *"4px base — the whole scale is a multiple of
 * this. No arbitrary values."* — so `px: '1px'` does not belong in it and `gap-[1px]` is
 * precisely the arbitrary value that sentence forbids. `tailwind.config.cjs` extends `gap`
 * with the hairline instead. ADR 9006 §4.
 *
 * ## The cell's ground is put on by the grid, not asked of the child
 *
 * `CellGrid` paints `bg-hairline` and lets the gap show it through; every child is wrapped
 * in a cell carrying `bg-surface-page`. A child that forgot its own background would
 * otherwise render as a hairline-coloured block, which reads as a rendering fault rather
 * than as a mistake anyone would look for. The wrapper costs one DOM node per cell and
 * this is a layout region, not a grid row — `bench:grid`'s budget is about
 * `PlanningGrid`, which never mounts one of these.
 *
 * ## No column count
 *
 * `CellGrid` takes a minimum cell width and lets `auto-fit` decide how many fit, so there
 * is no number of columns anywhere — which is also the only shape that survives G5
 * honestly. A screen that wants two columns on a wide viewport asks for a minimum of half
 * its content width and gets two, or gets one when the viewport cannot hold two.
 */

import { Children, isValidElement } from 'react';
import type { ReactNode } from 'react';

export interface BandProps {
  /**
   * A band is the page's horizontal unit. It runs the full width of the content region
   * and is separated from the next by a rule, never by a gutter.
   */
  readonly children: ReactNode;
  /**
   * `false` for the last band on a screen, where the rule would draw a line under the
   * page itself. Defaults to `true`, because a band in the middle of a stack is the
   * ordinary case and the exception should be the one that has to say so.
   */
  readonly ruled?: boolean;
  /**
   * `false` where the band's own child manages the horizontal inset — a `CellGrid`, whose
   * cells each carry their own padding, or a full-bleed heat map that is supposed to
   * reach the edges. Defaults to `true`.
   */
  readonly inset?: boolean;
}

export function Band({ children, ruled = true, inset = true }: BandProps): React.JSX.Element {
  return (
    <div
      className={[
        'min-w-0',
        inset ? 'px-8 py-6' : '',
        ruled ? 'border-hairline border-b' : '',
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}

export interface CellGridProps {
  readonly children: ReactNode;
  /**
   * The narrowest a cell may be before the grid drops to fewer of them. A CSS length, so
   * a screen can say `min(30rem, 100%)` and get one column on a phone without a
   * breakpoint. There is deliberately no column count — see the module docstring.
   */
  readonly minCellWidth?: string;
}

export function CellGrid({ children, minCellWidth = 'min(28rem, 100%)' }: CellGridProps): React.JSX.Element {
  return (
    <div
      className="bg-hairline gap-hairline grid"
      style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minCellWidth}, 1fr))` }}
    >
      {/* The grid wraps, rather than asking each child to carry the ground. A child that
          forgot would render as a hairline-coloured block — a rendering fault to look at,
          not a mistake anyone would go looking for. */}
      {Children.map(children, (child) => <Cell>{child}</Cell>)}
    </div>
  );
}

export interface CellProps {
  readonly children: ReactNode;
}

/**
 * One cell of a `CellGrid`. Carries the ground so the hairline behind the grid only shows
 * in the gaps, and nothing else — padding belongs to whatever the cell holds, which is
 * usually a `Panel variant="cell"`.
 */
export function Cell({ children }: CellProps): React.JSX.Element {
  return <div className="bg-surface-page flex min-w-0 flex-col">{children}</div>;
}

export interface SectionsProps {
  readonly children: ReactNode;
}

/** Whether a child asked to be a short section (`span="half"`, on `Panel` or anything else). */
function isHalf(child: ReactNode): boolean {
  return isValidElement(child) && (child.props as { span?: unknown }).span === 'half';
}

/**
 * A screen's stack of sections, where two CONSECUTIVE short ones sit side by side — audit v5b
 * D-05: "le sezioni corte stanno affiancate in una griglia a 2 colonne (sopra i 1200px) separate
 * da filetti; le sezioni lunghe vanno a tutta larghezza". A section says it is short with
 * `span="half"`; everything else is full width, in order. Pairs are taken left to right, so of
 * three halves in a row the first two pair and the third is full width, as is a half with no
 * half beside it: a lone half would bring back the empty right half this exists to remove.
 * Below `lg` every section is full width. `docs/adr/9028`.
 */
export function Sections({ children }: SectionsProps): React.JSX.Element {
  const items = Children.toArray(children);
  const out: ReactNode[] = [];
  for (let index = 0; index < items.length; index += 1) {
    const child = items[index];
    const next = items[index + 1];
    if (isHalf(child) && isHalf(next)) {
      out.push(
        <div
          key={`pair-${index}`}
          data-section-pair=""
          // A band of the page grid (brief part 3 §B6, ADR 9038): its two panels are its blocks, and
          // `align-items: stretch` (the grid's default) keeps them one height.
          data-layout-row="sections"
          className="border-hairline gap-x-gutter grid min-w-0 grid-cols-1 items-stretch lg:grid-cols-2 lg:[&>*+*]:border-s lg:[&>*+*]:ps-8"
        >
          {child}
          {next}
        </div>,
      );
      index += 1;
    } else {
      out.push(child);
    }
  }
  return <div className="flex min-w-0 flex-col gap-6">{out}</div>;
}
